import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import merge from 'lodash.merge';
import moment from 'moment';
import { history } from '../../configureStore';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { combineClassNames, debounce, replacePlaceholdersWithVariablesV2, getStringValueFromPath, handleOpenUrl, getSizesBasedOnScreen, executeFunction, getNestedValue, isClickInsidePortal, extractAllValuesInsideCurlyBraces} from '../../utils/base';
import { StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, Breakpoints, ActionChunk } from '../../types/base';
import { pageRequest, submitFormRequest, mergeStyleRequest, formValuesRequest, updateVariableRequest, actionRequest } from '../../store/page/actions';
import { CheckboxComponent as Type } from '../../types/base';
import generateComponentStyles from '../../utils/styleHelper';
import { getVariableValue, setVariableValue } from '../../utils/globalVariableHelper';

const StyledCheckboxLabel = styled.label`
    cursor: pointer;
    display: flex;
    align-items: center;
    jsutify-content: center;
    position: relative;
    z-index: 1;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates)}
`;

const StyledCheckbox = styled.input`
  cursor: pointer;
  margin: 0;
  pading: 0;
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;


interface CheckboxType extends Type {
  dataLake?: any;
  styleChunks?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const Checkbox: React.FC<CheckboxType & { title: string, pageSettings?: any }> = ({ 
  id, style: stDraft, value: val, events: eventsDraft, soc, pageSettings, dataLake, nestedTable, parentIndex, styleChunks, settings, 
  skeleton: renderSkeleton2, activeState: activeStateDraft,
  parentLoading, enableLocalShimmer,
  valueOriginal, cache
}) => {
  const { ref } = settings || {};
  const extractedValue = extractAllValuesInsideCurlyBraces(val || '');
  const value = dataLake?.data && typeof val === 'string' && val?.match(/{{this\..*}}/) ? getStringValueFromPath(dataLake.data, extractedValue) : val;
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const containerRef = useRef<HTMLLabelElement>(null);
  const variableData = useSelector(selectVariableData);
  const localVariableData = useSelector(selectLocalVariableData);
  const { style: styleDraft, events, activeState, ref: realRef } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, style: stDraft, events: eventsDraft, activeState: activeStateDraft }, dataLake.data) : { ref: undefined, style: stDraft, events: eventsDraft, activeState: activeStateDraft };
  const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
    const { indexes, refs } = chunks || {};
    let mergedStyle = style;
    
    // Apply index-based styles first
    if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
      mergedStyle = merge({}, mergedStyle, indexes[id][index]);
    }
    
    // Apply ref-based updates at the end
    if (refs && realRef) {
      const refUpdate = refs[realRef];
      if (refUpdate?.type && refUpdate.type === 'checkbox') {
        mergedStyle = merge({}, mergedStyle, refUpdate?.update);
      }
    }
    
    return mergedStyle;
  }, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (activeState === 'loading' || parentLoading) ? (cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal))) ? true : false : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, activeState, enableLocalShimmer, valueOriginal, parentLoading]);

  // First useMemo to merge active state styles with base style
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (activeState && styleDraft?.states?.[activeState]) {
      mergedStyle = merge({}, styleDraft, styleDraft.states[activeState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[activeState]) {
          acc[breakpoint] = merge({}, typedBreakpointStyle, typedBreakpointStyle.states[activeState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    return mergedStyle;
  }, [styleDraft, activeState]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => {
    return mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex);
  }, [mergedStyleWithState, styleChunks, parentIndex]);
  const [isChecked, setIsChecked] = useState(value === 'true' || value === true);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;

  const debouncedSubmitForm = useCallback(
    debounce(() => {
      dispatch(submitFormRequest({ 
        value: 'soc', 
        componentId: id
      }));
    }, 500),
    []
  );

  useEffect(() => {
    setIsChecked(value === 'true' || value === true);
  }, [value]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
    const fieldId = `${id}${nestedTable && typeof parentIndex === 'number' ? `::${parentIndex}` : ''}`;
    dispatch(formValuesRequest({ field: { id: realRef || fieldId, value: !isChecked } }));
    if (soc) debouncedSubmitForm();
  };

  const { 
    on_change: clickUpdates, 
    on_launch: launchUpdates, 
    on_hover: hoverUpdates, 
    on_unhover: unhoverUpdates,
    on_outclick: outclickUpdates,
  } = events || {};

  const dispatch = useDispatch();
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const internalFns = {
    $setVariable: (key: string, value: any) => {
      setVariableValue(key, value, (localKey, localValue) => {
        dispatch(updateVariableRequest({ variable: localKey, value: localValue }));
      });
    },
    $getVariable: (key: string) => {
      return getVariableValue(key, variableData, localVariableData);
    },
    $moment: (...args: any[]) => moment(...args)
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id 
      }, filterLoadingState));
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    handleCheckboxChange();
    if (clickUpdates) {
      e.stopPropagation();
  
      const styleUpdates = clickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = clickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = clickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = clickUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
        handleActionChunk(actionUpdates, filterLoadingState);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id,
            },
            filterLoadingState
          ));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ 
              value, silent, delay, componentId: id },
            filterLoadingState
            ));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  const handleHover = (e: React.MouseEvent<HTMLDivElement>) => {
    
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  
  }

  const handleUnhover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            // const newTimeout = setTimeout(() => {
            //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
            // }, config?.delay * 1000 || 0);

            // timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }

    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    
    const handleUnclick = (e: MouseEvent) => {
      if (activeState === 'disabled' || activeState === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        containerRef.current && 
        !containerRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        // Execute function updates
        if (filteredUpdates.functionUpdates.length) {
          filteredUpdates.functionUpdates.forEach((update) => {
            if (update?.function) {
              executeFunction(update.function, undefined, internalFns);
            }
          });
        }
        
        // Apply style updates
        if (filteredUpdates.styleUpdates.length) {
          dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
        }
        
        // Handle action updates
        if (filteredUpdates.actionUpdates.length) {
          handleActionChunk(filteredUpdates.actionUpdates);
        }
        
        // Process request updates
        if (filteredUpdates.requestUpdates.length) {
          filteredUpdates.requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ 
                value, 
                silent, 
                delay, 
                componentId: id 
              }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      }
    };
    
    document.addEventListener('mousedown', handleUnclick);
    return () => {
      document.removeEventListener('mousedown', handleUnclick);
    };
  }, [
    parentIndex, 
    activeState, 
    id,
    stDraft?.states?.loading,
    outclickUpdates?.length,
  ]);

  if (renderSceleton && style) {
    return <ContainerSkeleton 
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={activeState === 'disabled' || activeState === 'loading'}
    />;
  }

  return (
    <StyledCheckboxLabel
      ref={containerRef}
      htmlFor={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={activeState === 'disabled' || activeState === 'loading'}
      data-component="checkbox"
      className={combineClassNames(style.class)}
      onClick={handleClick}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
    >
      <StyledCheckbox
        data-id={realRef || id}
        id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
        type="checkbox"
      />
          {isChecked
            ? (settings?.icons?.active ? <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={settings.icons.active} />  : <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scale(0.9)', overflow: 'visible' }}>
              <path d="M0.5 4C0.5 2.067 2.067 0.5 4 0.5H12C13.933 0.5 15.5 2.067 15.5 4V12C15.5 13.933 13.933 15.5 12 15.5H4C2.067 15.5 0.5 13.933 0.5 12V4Z" stroke={style?.font?.color || 'white'} />
              <path d="M12 5L6.5 10.5L4 8" stroke={style?.font?.color || 'white'} strokeWidth="1.6666" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>)
            : (settings?.icons?.inactive ? <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={settings.icons.inactive} /> : <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scale(0.9)', overflow: 'visible' }}>
              <path d="M0.5 4C0.5 2.067 2.067 0.5 4 0.5H12C13.933 0.5 15.5 2.067 15.5 4V12C15.5 13.933 13.933 15.5 12 15.5H4C2.067 15.5 0.5 13.933 0.5 12V4Z" stroke={style?.font?.color || 'white'} />
            </svg>)
          }
    </StyledCheckboxLabel>
  );
}

export default Checkbox;
